import { Injectable } from '@angular/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

/** IndexedDB database name — one database for the whole app. */
export const IDB_NAME = 'tinite_mobile';

/**
 * Schema version. Bump ONLY when adding/removing an object store, and extend
 * {@link IdbStorageService.upgrade} to create the new store — an existing
 * install runs `onupgradeneeded` with the old stores already present, so
 * every store creation must be guarded by `objectStoreNames.contains()`.
 */
export const IDB_VERSION = 3;

/** Object store holding the offline submission queue (`UpqueueService`). */
export const IDB_STORE_QUEUE = 'queue';

/** Object store holding pending evidence photos (`PhotoService`). */
export const IDB_STORE_PHOTOS = 'photos';

/**
 * Object store for **app-level** settings shared across users:
 * `app_settings_cache` (`AppSettingsService`).
 *
 * @remarks
 * Separate from {@link IDB_STORE_CACHE} for one reason: it must **survive
 * logout**. `AuthService.clearAllStorage()` has always exempted
 * `app_settings_cache` from its sweep ("app settings is shared across users"),
 * and `AppSettingsService.restore()` reading it synchronously on boot is what
 * keeps a returning device off the first-run splash screen (see `app.ts`).
 * Grouping it with the per-user caches would have wiped it on every logout —
 * so it is excluded from {@link IDB_STORES_CLEARED_ON_LOGOUT}.
 */
export const IDB_STORE_SETTINGS = 'settings';

/**
 * Object store for **per-user** cached data that is re-fetchable from the API
 * (2026-08-05): `{hash}_cache_jobs` + `{hash}_cache_jobs_at` (`JobsService`)
 * and `{hash}_ref_data_cache` (`ReferenceDataService`).
 *
 * @remarks
 * Grouped into one store because they share a lifecycle — both are entity-scoped
 * caches, both are cleared on logout, and neither is ever queried by anything
 * but its own exact key. The original SecureStorage key strings are reused
 * verbatim as IndexedDB keys, so the migration is a medium change only.
 *
 * These moved off `capacitor-secure-storage-plugin` for the same reason the
 * queue and photos did: its browser fallback is `localStorage` (~5 MB
 * per-origin, `btoa()`-inflated), and the job cache plus reference data are
 * large enough to matter against that budget. On native the plugin's
 * Keychain/Keystore backing is lost for these keys — accepted, since all three
 * are non-secret and re-fetchable. The genuinely sensitive values (auth tokens,
 * and the signature image — see {@link IDB_STORE_SIGNATURE}) are handled
 * differently.
 */
export const IDB_STORE_CACHE = 'cache';

/**
 * Object store for the process server's reusable signature image
 * (`SignatureStorageService`, `{hash}_esign_data`) — **web/PWA only**.
 *
 * @remarks
 * Deliberately NOT used on native. `SignatureStorageService` stores a
 * *plaintext* base64 PNG of the officer's signature, and on native
 * `capacitor-secure-storage-plugin` keeps it in the iOS Keychain / Android
 * `KeyStore`-wrapped `SharedPreferences` — the only at-rest protection that
 * value has. IndexedDB in a WebView offers none of it, and native has no quota
 * problem to solve, so moving it there would be a pure security downgrade.
 *
 * On web the plugin's fallback is already `localStorage` + `btoa`, which
 * protects nothing, so switching to IndexedDB there costs no security and buys
 * the quota headroom. Hence the platform split in
 * `SignatureStorageService` — see its own doc comment.
 */
export const IDB_STORE_SIGNATURE = 'signature';

/**
 * Object store holding the device-bound, non-extractable AES-GCM key(s) used
 * by `DeviceCryptoService` (2026-08-05).
 *
 * @remarks
 * Deliberately a dedicated store, not co-located inside {@link IDB_STORE_SIGNATURE}
 * — `DeviceCryptoService` started as signature-only (hence that store's name)
 * but is now also used by `AffidavitAdapter`/`UpqueueAdapter` to encrypt the
 * signed affidavit document at rest in {@link IDB_STORE_QUEUE}. Keeping the key
 * material in a neutrally-named store avoids the naming becoming misleading as
 * more features reuse the same device key, even though co-locating would have
 * been just as *safe* (a non-extractable `CryptoKey` can't be exfiltrated by
 * reading whatever store holds it).
 */
export const IDB_STORE_CRYPTO_KEYS = 'cryptoKeys';

/** Every store created by {@link IdbStorageService.upgrade}. */
export const IDB_STORES = [
  IDB_STORE_QUEUE,
  IDB_STORE_PHOTOS,
  IDB_STORE_SETTINGS,
  IDB_STORE_CACHE,
  IDB_STORE_SIGNATURE,
  IDB_STORE_CRYPTO_KEYS,
] as const;

/**
 * Stores emptied by `AuthService.clearAllStorage()` on logout — everything
 * except {@link IDB_STORE_SETTINGS}.
 *
 * @remarks
 * Deliberately NOT `IDB_STORES`. App settings are shared across users and must
 * outlive a logout (see {@link IDB_STORE_SETTINGS}); every other store holds
 * per-user data that must not be readable by the next officer on a shared
 * device. If a new store is added, decide explicitly which list it belongs in.
 *
 * {@link IDB_STORE_CRYPTO_KEYS} is included: clearing it alongside
 * {@link IDB_STORE_SIGNATURE} and {@link IDB_STORE_QUEUE} (both of which are
 * also wiped here) is safe and deliberate — nothing encrypted with the old key
 * survives the same logout to become orphaned/undecryptable, and the next use
 * simply generates a fresh key lazily.
 */
export const IDB_STORES_CLEARED_ON_LOGOUT = [
  IDB_STORE_QUEUE,
  IDB_STORE_PHOTOS,
  IDB_STORE_CACHE,
  IDB_STORE_SIGNATURE,
  IDB_STORE_CRYPTO_KEYS,
] as const;

/** A store name known to this database. */
export type IdbStore = (typeof IDB_STORES)[number];

/**
 * Minimal promise wrapper over IndexedDB, plus a migration bridge off
 * `capacitor-secure-storage-plugin`. Backs everything this app persists locally
 * except the auth tokens: the offline submission queue, pending evidence
 * photos, the app-settings / job / reference-data caches, and (on web only) the
 * saved signature.
 *
 * @remarks
 * **Why this exists (2026-08-05).** Both were previously written to
 * `localStorage` — the queue indirectly, via `capacitor-secure-storage-plugin`,
 * whose browser fallback is `localStorage.setItem(key, btoa(value))`. That
 * caps the pair at a shared ~5 MB per-origin budget and adds a +33 % `btoa()`
 * surcharge on the queue, which a single base64 photo was enough to exhaust
 * (`QuotaExceededError`, confirmed live in UAT). IndexedDB's quota is a share
 * of real free disk — gigabytes on any real device — and values are stored via
 * structured clone, so no JSON string or base64 wrapper is needed at all.
 *
 * **Deliberately dependency-free.** Written against the raw IndexedDB API
 * rather than adding `idb`/`dexie`, because `CLAUDE.md` documents `npm install`
 * having previously left this repo's `node_modules` incomplete; the surface
 * needed here (get/set/remove/clear/keys on a plain key-value store) is small
 * enough not to justify that risk.
 *
 * **Native platforms are unaffected either way** — `capacitor-secure-storage-plugin`
 * uses Keychain/SharedPreferences there, which have no quota. IndexedDB is
 * still used on native for consistency (a single code path is easier to reason
 * about than a per-platform split), and works fine inside both WKWebView and
 * Android WebView.
 *
 * **Every method degrades instead of throwing.** {@link supported} is `false`
 * when `indexedDB` is missing entirely (jsdom, some private-browsing modes),
 * and reads resolve to `null` / writes reject so callers can fall back to
 * their previous storage. Callers MUST handle that — see
 * `UpqueueService.writeQueue()` and `PhotoService.persist()`, which both fall
 * back to their legacy backend rather than losing data.
 *
 * **Durability caveat, unchanged by this service:** on iOS Safari, site data
 * for a non-installed site can be evicted after ~7 days without interaction.
 * That applied to `localStorage` too, so this is not a regression, but
 * IndexedDB does not make an offline queue durable there. Only an installed
 * PWA or the native app is safe for multi-day offline work.
 */
@Injectable({ providedIn: 'root' })
export class IdbStorageService {
  /** Cached open-database promise, so the database is opened at most once. */
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Whether IndexedDB is usable in this environment.
   *
   * @remarks
   * Read as a getter rather than cached at construction so a test can install
   * a shim after the service is created. Accessing `indexedDB` is wrapped
   * because some hardened/private-mode browsers throw on property access
   * rather than simply leaving it undefined.
   */
  get supported(): boolean {
    try {
      return typeof indexedDB !== 'undefined' && indexedDB !== null;
    } catch {
      return false;
    }
  }

  /**
   * Reads a value by key.
   *
   * @param store - Object store to read from.
   * @param key - Key to read.
   * @returns The stored value, or `null` when absent or IndexedDB is unusable.
   */
  async get<T>(store: IdbStore, key: string): Promise<T | null> {
    if (!this.supported) return null;
    try {
      const db = await this.open();
      return await new Promise<T | null>((resolve, reject) => {
        const tx  = db.transaction(store, 'readonly');
        const req = tx.objectStore(store).get(key);
        req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
        req.onerror   = () => reject(req.error);
      });
    } catch {
      return null;
    }
  }

  /**
   * Writes a value, replacing any existing entry for the key.
   *
   * @remarks
   * The value is persisted by structured clone — pass the object directly, do
   * NOT `JSON.stringify` it first. Rejects when IndexedDB is unusable or the
   * transaction fails (including a genuine quota exhaustion), so callers can
   * fall back; never fails silently.
   *
   * @param store - Object store to write to.
   * @param key - Key to write.
   * @param value - Structured-cloneable value.
   */
  async set(store: IdbStore, key: string, value: unknown): Promise<void> {
    if (!this.supported) throw new Error('IndexedDB unavailable');
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(store, 'readwrite');
      tx.objectStore(store).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror    = () => reject(tx.error);
      tx.onabort    = () => reject(tx.error);
    });
  }

  /**
   * Deletes a single key. Resolves successfully when the key is already absent.
   *
   * @param store - Object store to delete from.
   * @param key - Key to delete.
   */
  async remove(store: IdbStore, key: string): Promise<void> {
    if (!this.supported) return;
    try {
      const db = await this.open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror    = () => reject(tx.error);
        tx.onabort    = () => reject(tx.error);
      });
    } catch {
      // Nothing recoverable to do — the caller is deleting, not reading.
    }
  }

  /**
   * Empties an entire object store.
   *
   * Used by `AuthService.clearAllStorage()` on logout — see that method for
   * why the offline queue is deliberately included in the logout wipe.
   *
   * @param store - Object store to empty.
   */
  async clearStore(store: IdbStore): Promise<void> {
    if (!this.supported) return;
    try {
      const db = await this.open();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(store, 'readwrite');
        tx.objectStore(store).clear();
        tx.oncomplete = () => resolve();
        tx.onerror    = () => reject(tx.error);
        tx.onabort    = () => reject(tx.error);
      });
    } catch {
      // Best-effort cleanup.
    }
  }

  /**
   * Lists every key in a store.
   *
   * @param store - Object store to enumerate.
   * @returns All keys, or `[]` when the store is empty or unreadable.
   */
  async keys(store: IdbStore): Promise<string[]> {
    if (!this.supported) return [];
    try {
      const db = await this.open();
      return await new Promise<string[]>((resolve, reject) => {
        const tx  = db.transaction(store, 'readonly');
        const req = tx.objectStore(store).getAllKeys();
        req.onsuccess = () => resolve((req.result as IDBValidKey[]).map(String));
        req.onerror   = () => reject(req.error);
      });
    } catch {
      return [];
    }
  }

  // ─── Legacy-SecureStorage bridge ─────────────────────────────────────────
  //
  // The four keys migrated on 2026-08-05 (app settings, job cache, reference
  // data, signature) all need the same three things: read-with-migration,
  // write-with-fallback, and delete-from-both. These helpers exist so each
  // service does not hand-roll that — `UpqueueService.readQueue()`/
  // `writeQueue()` predate them and keep their own copy, because they layer
  // queue-specific Sentry reporting on top; do not "unify" them without
  // preserving that.

  /**
   * Reads a value, transparently migrating a legacy `SecureStoragePlugin`
   * entry into IndexedDB the first time it is seen.
   *
   * @remarks
   * A build before 2026-08-05 wrote these keys through the plugin. On the first
   * launch after upgrading, IndexedDB is empty while real cached data still
   * sits under the old key — reading IndexedDB alone would silently discard it
   * (for `{hash}_cache_jobs` that means the officer's offline job list
   * disappears until the next successful sync). The legacy copy is removed only
   * after the IndexedDB write succeeds, so an interrupted migration retries on
   * the next launch instead of losing data.
   *
   * @param store - Object store to read from.
   * @param key - Storage key, identical to the original SecureStorage key.
   * @param decodeLegacy - Parses the legacy plugin's raw string value. Throwing
   *                       here is treated as "absent" (corrupt legacy data).
   * @returns The stored value, or `null` when neither backend has one.
   */
  async getOrMigrate<T>(
    store: IdbStore,
    key: string,
    decodeLegacy: (raw: string) => T,
  ): Promise<T | null> {
    const fromIdb = await this.get<T>(store, key);
    if (fromIdb !== null) return fromIdb;

    let legacy: T;
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      legacy = decodeLegacy(value);
    } catch {
      return null; // Absent in both, or unparseable legacy data.
    }

    try {
      await this.set(store, key, legacy);
      await SecureStoragePlugin.remove({ key });
    } catch {
      // Migration failed — leave the legacy copy so the next launch retries.
      // The value is still returned and fully usable this session.
    }
    return legacy;
  }

  /**
   * Writes a value to IndexedDB, falling back to `SecureStoragePlugin` when
   * IndexedDB is unusable.
   *
   * @remarks
   * The fallback matters for private-browsing modes that disable IndexedDB
   * outright — without it these caches would simply stop persisting. It writes
   * the same string shape the pre-migration code did, so
   * {@link getOrMigrate} reads it back either way.
   *
   * Never throws: these are all re-fetchable caches, so a failed write costs a
   * cold start, not data. (The offline queue is the opposite case and is
   * handled by `UpqueueService.persistOrWarn()`, which reports failures.)
   *
   * @param store - Object store to write to.
   * @param key - Storage key.
   * @param value - Structured-cloneable value for IndexedDB.
   * @param encodeLegacy - Produces the string written by the fallback path.
   */
  async setOrFallback(
    store: IdbStore,
    key: string,
    value: unknown,
    encodeLegacy: () => string,
  ): Promise<void> {
    try {
      await this.set(store, key, value);
      return;
    } catch {
      // Fall through to the legacy backend.
    }
    try {
      await SecureStoragePlugin.set({ key, value: encodeLegacy() });
    } catch {
      // Both unavailable — non-fatal for a re-fetchable cache.
    }
  }

  /**
   * Deletes a key from IndexedDB **and** its legacy `SecureStoragePlugin`
   * copy.
   *
   * @remarks
   * Both are required: a stale legacy entry left behind would be re-migrated
   * by {@link getOrMigrate} on the next read, silently resurrecting data that
   * was meant to be cleared (e.g. another user's job cache after logout).
   *
   * @param store - Object store to delete from.
   * @param key - Storage key.
   */
  async removeEverywhere(store: IdbStore, key: string): Promise<void> {
    await this.remove(store, key);
    try { await SecureStoragePlugin.remove({ key }); } catch { /* already gone */ }
  }

  /**
   * Opens (and caches) the database connection.
   *
   * @remarks
   * A failed open clears {@link dbPromise} so a later call can retry rather
   * than being permanently poisoned by one transient failure. `onblocked`
   * rejects instead of hanging — it fires when another tab holds an older
   * version open, and waiting indefinitely there would stall every caller.
   */
  private open(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(IDB_NAME, IDB_VERSION);
      req.onupgradeneeded = () => this.upgrade(req.result);
      req.onsuccess       = () => resolve(req.result);
      req.onerror         = () => reject(req.error);
      req.onblocked       = () => reject(new Error('IndexedDB open blocked'));
    }).catch((err: unknown) => {
      this.dbPromise = null;
      throw err;
    });
    return this.dbPromise;
  }

  /**
   * Creates any object store missing from the database.
   *
   * @remarks
   * Guarded by `contains()` per store: on an upgrade from an earlier
   * {@link IDB_VERSION} the already-created stores are still present, and
   * calling `createObjectStore` for an existing name throws.
   *
   * @param db - The database being upgraded.
   */
  private upgrade(db: IDBDatabase): void {
    for (const store of IDB_STORES) {
      if (!db.objectStoreNames.contains(store)) db.createObjectStore(store);
    }
  }
}
